import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getClient } from '../services/clientsService';
import { listInvoices } from '../services/invoicesService';
import { listSales } from '../services/salesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function ClientDetailPage() {
  const { id } = useParams();
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [comprobantes, setComprobantes] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await getClient(id);
      setClient(c);
      // Solo al abrir el detalle (lazy)
      const [inv, sales] = await Promise.all([
        listInvoices({ ruc, pageSize: 10, search: String(c.nroDocumento || '') }),
        listSales({ ruc, pageSize: 10, search: String(c.nroDocumento || c.razonSocial || '') }),
      ]);
      setComprobantes(inv.items);
      setPedidos(sales.items);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id, ruc]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando cliente…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!client) return <EmptyState title="Cliente no encontrado" />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/clientes">Clientes</Link> / {client.nroDocumento}
          </p>
          <h1>{client.razonSocial || client.nombre || client.nombreComercial || 'Cliente'}</h1>
          <p className="muted">
            {client.tipoDocumento || '—'} · {client.nroDocumento || '—'}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
          {client.ruc !== 'GLOBAL' ? (
            <Link className="btn btn-primary" to={`/admin/clientes/${id}/editar`}>
              Editar
            </Link>
          ) : null}
        </div>
      </header>

      <section className="meta-grid">
        <div>
          <span className="muted">Dirección</span>
          <div>{client.direccion || '—'}</div>
        </div>
        <div>
          <span className="muted">Ubigueo</span>
          <div>{client.ubigueo || '—'}</div>
        </div>
        <div>
          <span className="muted">Teléfono</span>
          <div>{client.telefono || '—'}</div>
        </div>
        <div>
          <span className="muted">Email</span>
          <div>{client.email || '—'}</div>
        </div>
        <div>
          <span className="muted">Ámbito</span>
          <div>
            {client.ruc === 'GLOBAL' || client.isGlobal ? (
              <Badge tone="info">Global</Badge>
            ) : (
              <Badge tone="ok">Empresa</Badge>
            )}
          </div>
        </div>
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Comprobantes relacionados</h2>
          <Link to="/admin/comprobantes">Ver todos</Link>
        </header>
        {comprobantes.length === 0 ? (
          <EmptyState title="Sin comprobantes en la última consulta" />
        ) : (
          <ul className="simple-list">
            {comprobantes.map((c) => (
              <li key={`${c.collection}-${c.id}`}>
                <div>
                  <strong>
                    {c.serie}-{c.correlativo}
                  </strong>
                  <div className="muted">
                    {c.tipoLabel} · {formatDate(c.fechaEmision)}
                  </div>
                </div>
                <span>{formatMoney(c.total)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Ventas (pedidos)</h2>
          <Link to="/admin/ventas">Ver ventas</Link>
        </header>
        {pedidos.length === 0 ? (
          <EmptyState title="Sin pedidos en la última consulta" />
        ) : (
          <ul className="simple-list">
            {pedidos.map((p) => (
              <li key={p.id}>
                <div>
                  <strong>Pedido #{p.pedido}</strong>
                  <div className="muted">
                    {formatDate(p.fecha)} · {p.status || '—'}
                  </div>
                </div>
                <span>{formatMoney(p.total)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
